import { Fragment, useState } from 'react';
import { Checkbox, Divider } from 'antd';
import { ChevronDownIcon } from '@/assets/icons/icons';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import type { ListItem } from '../ControlTower/types';
import type { AlertAssignmentOption } from './utils/alertAssignmentOptions';

type Props = {
  source: AlertAssignmentOption[];
  searchQuery: string;
  selectedOptions: ListItem[];
  setSelectedOptions?: (options: ListItem[]) => void;
  hiddenOptionIds?: Set<string>;
};

/** Names show their first 20 characters, then "..." and a tooltip. */
const NAME_MAX_CHARS = 20;

export default function AlertAssignmentSubFilterMenuBody({
  source,
  searchQuery,
  selectedOptions,
  setSelectedOptions,
  hiddenOptionIds,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const query = searchQuery.trim().toLowerCase();
  const matches = (item: ListItem) =>
    (item.name ?? '').toLowerCase().includes(query);
  const visible = source.filter((item) => !hiddenOptionIds?.has(item.id));
  const roots = visible.filter(
    (item) =>
      !item.parentId &&
      (matches(item) ||
        visible.some((child) => child.parentId === item.id && matches(child)))
  );
  const selectedIds = new Set(selectedOptions.map((item) => item.id));
  const toggle = (item: ListItem) =>
    setSelectedOptions?.(
      selectedIds.has(item.id)
        ? selectedOptions.filter((option) => option.id !== item.id)
        : [...selectedOptions, { id: item.id, name: item.name }]
    );
  const checkbox = (item: AlertAssignmentOption) => (
    <Checkbox
      aria-label={item.name}
      className='custom-menu-shipment-checkbox-only checkbox-sm alert-assignment-checkbox'
      checked={selectedIds.has(item.id)}
      disabled={item.enabled === false}
      onChange={() => toggle(item)}
    >
      {item.kind === 'team' && (
        <span
          aria-hidden
          className='alert-assignment-team-dot'
          // The dot takes the colour of the team, which is only known at runtime.
          // eslint-disable-next-line react/forbid-dom-props
          style={{ background: item.color }}
        />
      )}
      <TruncateWithTooltip
        text={item.name}
        max={NAME_MAX_CHARS}
        render={(text) => <span className='alert-assignment-name'>{text}</span>}
      />
    </Checkbox>
  );
  return (
    <div className='alert-assignment-options'>
      {roots.map((item, index) => {
        const children = visible.filter((child) => child.parentId === item.id);
        const open = !!query || expanded.has(item.id);
        const groupStart = index > 0 && roots[index - 1].kind !== item.kind;
        return (
          <Fragment key={item.id}>
            {groupStart && <Divider className='alert-assignment-divider' />}
            <div className='alert-assignment-group'>
              <div className='alert-assignment-row'>
                {checkbox(item)}
                {item.kind === 'team' &&
                  (children.length ? (
                    <button
                      type='button'
                      className='alert-assignment-chevron'
                      aria-label={`${open ? 'Collapse' : 'Expand'} ${item.name}`}
                      aria-expanded={open}
                      onClick={() =>
                        setExpanded((previous) => {
                          const next = new Set(previous);
                          if (next.has(item.id)) next.delete(item.id);
                          else next.add(item.id);
                          return next;
                        })
                      }
                    >
                      <ChevronDownIcon
                        className={`chevron-icon ${open ? 'rotated' : ''}`}
                      />
                    </button>
                  ) : (
                    <span className='alert-assignment-no-users'>No users</span>
                  ))}
              </div>
              {open &&
                children
                  .filter((child) => matches(item) || matches(child))
                  .map((child) => (
                    <div key={child.id} className='alert-assignment-child'>
                      {checkbox(child)}
                    </div>
                  ))}
            </div>
          </Fragment>
        );
      })}
      {!roots.length && (
        <div className='alert-assignment-empty'>
          {source.length ? 'No matching owners' : 'No owners available'}
        </div>
      )}
    </div>
  );
}
